/**
 * Pantalla de Registro de Calificaciones
 * 
 * Pantalla para gestionar el registro de calificaciones por grupo y evaluación.
 * Sigue el patrón unificado del sistema con componentes reutilizables.
 * 
 * Funcionalidades:
 * - Búsqueda de grupos por campus y tipo de evaluación
 * - Registro de calificaciones por estudiante
 * - Generación de reportes de calificaciones
 * - Detección automática del asesor basado en el usuario logueado
 * 
 * Basado en Figma ID: 6401eeee
 */

import React, { useState, useEffect } from "react";
import styles from "../../styles/CampusStyles/PantallaDeCampus.module.css";
import calificacionesStyles from "./RegistroCalificaciones.module.css";
import Sidebar from "../../components/Sidebar";
import HeaderGlobal from "../../components/HeaderGlobal";
import BusquedaGrupo from "./BusquedaGrupo";
import RegistroGrupo from "./RegistroGrupo";
import ReporteCalificacionesModal from "./ReporteCalificacionesModal";
import { getInfoAsesorUsuario } from "../../api/calificaciones";
import { useAuth } from "../../api/AuthContext";

function RegistroCalificacionesScreen() {
    // Estado para estudiantes y calificaciones del grupo actual
    const [estudiantesReporte, setEstudiantesReporte] = useState([]);
  const { user } = useAuth();
  
  // Estado para vista actual (búsqueda o registro)
  const [vistaActual, setVistaActual] = useState('busqueda'); // 'busqueda' | 'registro'
  
  // Estado para grupo seleccionado
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  
  // Estado para tipo de evaluación seleccionada
  const [tipoEvaluacion, setTipoEvaluacion] = useState(null);
  
  // Estado para modal de reporte
  const [mostrarReporte, setMostrarReporte] = useState(false);
  
  // Estado para tipos de evaluación disponibles
  const [tiposEvaluacion, setTiposEvaluacion] = useState([]);
  
  // Estado para información del asesor/usuario
  const [infoUsuario, setInfoUsuario] = useState({
    usuario: null,
    asesor: null,
    institucion: null
  });
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [asesorRetryCount, setAsesorRetryCount] = useState(0);

  // Estado para campus seleccionado desde HeaderGlobal
  const [campusSeleccionado, setCampusSeleccionado] = useState("");

  // Cargar información del asesor/usuario al iniciar (con soporte de reintento)
  useEffect(() => {
    let cancelled = false;
    
    const cargarInfoAsesor = async () => {
      try {
        setLoadingInfo(true);
        const data = await getInfoAsesorUsuario();
        if (cancelled) return;
        console.log('[RegistroCalificaciones] Info asesor cargada:', data);
        console.log('[RegistroCalificaciones] DEBUG INFO:', data.debug);
        if (data.debug?.asesores_disponibles) {
          console.log('[RegistroCalificaciones] Asesores disponibles en BD:', data.debug.asesores_disponibles);
        }
        if (data.debug?.metodo_encontrado) {
          console.log('[RegistroCalificaciones] Método de detección usado:', data.debug.metodo_encontrado);
        }
        if (data.debug?.emails_buscados) {
          console.log('[RegistroCalificaciones] Emails buscados:', data.debug.emails_buscados);
        }
        setInfoUsuario({
          usuario: data.usuario,
          asesor: data.asesor,
          institucion: data.institucion
        });
      } catch (error) {
        if (cancelled) return;
        console.error('[RegistroCalificaciones] Error al cargar info asesor:', error);
        // Usar datos del contexto de autenticación como fallback
        setInfoUsuario({
          usuario: { nombre: user?.nombre_completo || user?.email || 'Usuario' },
          asesor: null,
          institucion: { nombre: 'Universidad Lux' }
        });
      } finally {
        if (!cancelled) setLoadingInfo(false);
      }
    };
    
    cargarInfoAsesor();
    
    return () => { cancelled = true; };
  }, [user, asesorRetryCount]);

  // Handler para seleccionar un grupo desde la búsqueda
  const handleSeleccionarGrupo = (grupo, evaluacion, tiposEval) => {
    console.log('[RegistroCalificaciones] Grupo seleccionado:', grupo, 'Evaluación:', evaluacion);
    setGrupoSeleccionado(grupo);
    setTipoEvaluacion(evaluacion);
    if (tiposEval) {
      setTiposEvaluacion(tiposEval);
    }
    setVistaActual('registro');
  };

  // Handler para cambiar tipo de evaluación desde la captura
  const handleCambioTipoEvaluacion = (nuevoTipo) => {
    console.log('[RegistroCalificaciones] Cambio de tipo evaluación:', nuevoTipo);
    setTipoEvaluacion(nuevoTipo);
  };

  // Handler para regresar a la búsqueda
  const handleRegresar = () => {
    setVistaActual('busqueda');
    setGrupoSeleccionado(null);
    setTipoEvaluacion(null);
  };

  // Handler para abrir modal de reporte
  // Handler para abrir modal de reporte, recibe estudiantes
  const handleAbrirReporte = (estudiantes) => {
    setEstudiantesReporte(estudiantes);
    setMostrarReporte(true);
  };

  // Handler para cerrar modal de reporte
  const handleCerrarReporte = () => {
    setMostrarReporte(false);
  };

  return (
    <main className={styles.campusScreen}>
      <div className={styles.mainLayout}>
        {/* Sidebar */}
        <aside className={styles.sidebarColumn}>
          <Sidebar />
        </aside>

        <div className={styles.contentColumn}>
          <div className={styles.contentWrapper}>
            {/* Header global reutilizable */}
            <HeaderGlobal title="Registro de Calificaciones" onCampusChange={campusId => setCampusSeleccionado(campusId)} />
            {/* Contenido principal */}
            <section className={styles.mainContent}>
              {/* ...se eliminó la sección de información del usuario en esquina superior derecha... */}

              {/* Contenido dinámico según vista actual */}
              <div className={styles.tableSection}>
                {vistaActual === 'busqueda' ? (
                  <BusquedaGrupo 
                    onSeleccionarGrupo={handleSeleccionarGrupo}
                    infoAsesor={infoUsuario.asesor}
                    infoUsuario={infoUsuario.usuario}
                  />
                ) : (
                  <RegistroGrupo 
                    grupo={grupoSeleccionado}
                    tipoEvaluacion={tipoEvaluacion}
                    tiposEvaluacion={tiposEvaluacion}
                    onCambioTipoEvaluacion={handleCambioTipoEvaluacion}
                    onRegresar={handleRegresar}
                    onAbrirReporte={handleAbrirReporte}
                  />
                )}
              </div>
            </section>

            {/* Modal de Reporte de Calificaciones */}
            {mostrarReporte && (
              <ReporteCalificacionesModal
                grupo={grupoSeleccionado}
                estudiantes={estudiantesReporte}
                onCerrar={handleCerrarReporte}
              />
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

export default RegistroCalificacionesScreen;
